import DashboardContent from '@admin/dashboard/onboardingGuide/DashboardContent'
import { useApps } from '@auth/useApps'
import { AppPermission } from '@shared/permissions'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { useOnboardingGuide } from '../../hooks/useOnboardingGuide'
import OnboardingGuideDialog from './OnboardingGuideDialog'
import OnboardingGuideFab from './OnboardingGuideFab'

const OnboardingGuideWrapper: React.FC = () => {
  const location = useLocation()
  const {
    isDialogOpen,
    shouldShowFab,
    openDialog,
    closeDialog,
    dismissCurrentGuide,
    guideContent,
  } = useOnboardingGuide()
  const [fabElement, setFabElement] = useState<HTMLButtonElement | null>(null)
  const [effectiveDialogOpen, setEffectiveDialogOpen] = useState(false)
  const [showDashboardContent, setShowDashboardContent] = useState(false)
  const [hasAppPermission] = useApps()
  const canManageOnboardingGuide = hasAppPermission(
    'onboardingApp',
    AppPermission.OnboardingAppManage,
  )

  // Callback ref to capture the FAB element when it mounts
  const fabRef = useCallback((element: HTMLButtonElement | null) => {
    setFabElement(element)
  }, [])

  // Only open dialog when both isDialogOpen is true AND fabElement is available
  useEffect(() => {
    if (isDialogOpen && fabElement) {
      setEffectiveDialogOpen(true)
    } else if (!isDialogOpen) {
      setEffectiveDialogOpen(false)
    }
  }, [isDialogOpen, fabElement])

  // Reset to guide content when location changes (navigation happens)
  useEffect(() => {
    setShowDashboardContent(false)
  }, [location.pathname])

  const handleClose = () => {
    setEffectiveDialogOpen(false)
    closeDialog()
    // Reset to guide content when dialog closes
    setShowDashboardContent(false)
  }

  const handleDismissGuide = async () => {
    setEffectiveDialogOpen(false)
    await dismissCurrentGuide()
  }

  const handleBack = () => {
    setShowDashboardContent(true)
  }

  if (!shouldShowFab || !guideContent || !canManageOnboardingGuide) {
    return null
  }

  return (
    <>
      <OnboardingGuideFab ref={fabRef} onClick={openDialog} isDialogOpen={isDialogOpen} />
      <OnboardingGuideDialog
        open={effectiveDialogOpen}
        onClose={handleClose}
        helpUrl={showDashboardContent ? undefined : guideContent.helpUrl}
        routeElementId={guideContent.routeElementId}
        content={
          showDashboardContent ? (
            <DashboardContent
              onDismissGuide={handleDismissGuide}
              isEmbedded={true}
              onAlreadyOnActionUrl={() => setShowDashboardContent(false)}
            />
          ) : (
            guideContent.content
          )
        }
        onBack={showDashboardContent ? undefined : handleBack}
        onDismissGuide={showDashboardContent ? undefined : handleDismissGuide}
        anchorEl={fabElement}
      />
    </>
  )
}

export default OnboardingGuideWrapper
